'use client';

import { useId } from 'react';
import { useParams } from 'next/navigation';
import { useTranslations } from 'use-intl';
import { CurrencyHelpers } from '@/utils/currency-helpers';

interface FisProgressBarProps {
  /** Net items subtotal BEFORE discounts (ex-GST, ex-freight), in centAmount. Used for FIS threshold comparison. */
  subtotal: number;
  /** BU-specific threshold in centAmount. */
  threshold: number;
  currencyCode: string;
  fractionDigits?: number;
}

const FisProgressBar = ({ subtotal, threshold, currencyCode, fractionDigits = 2 }: FisProgressBarProps) => {
  const translate = useTranslations();
  const params = useParams();
  const locale = typeof params.locale === 'string' ? params.locale : 'en-AU';
  const labelId = useId();
  const qualifying = subtotal >= threshold;
  const pct = Math.max(0, Math.min(Math.round((subtotal / threshold) * 100), 100));

  const fmt = (cents: number) => CurrencyHelpers.formatForCurrency(cents, locale, currencyCode, fractionDigits);

  return (
    <div className="mb-5">
      {qualifying ? (
        <p id={labelId} className="text-primary text-13 mb-2 font-bold">
          {translate('cart.fis-qualifying')}
        </p>
      ) : (
        <p id={labelId} className="text-13 mb-2 text-gray-700">
          {translate('cart.fis-below-threshold', { amount: fmt(threshold - subtotal) })}
        </p>
      )}
      <div
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-labelledby={labelId}
        className="h-2 w-full overflow-hidden rounded-full bg-gray-200"
      >
        <div className="bg-primary h-full rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default FisProgressBar;
